<script setup lang="ts">
import { computed, inject } from "vue";
import { nameInjectionKey } from "../keys";
import { useVuelidate, ValidationArgs } from "@vuelidate/core";

const props = withDefaults(defineProps<{
    title: string,
    id?: string | undefined,
    value: unknown,
    childClass?: string,
    radioType?: "standard" | "pill" | "block" | "responsive",
    modelValue: unknown,
    validation?: ValidationArgs,
    disabled?: boolean
}>(), {
    id: undefined,
    childClass: undefined,
    radioType: "standard",
    validation: undefined,
});

const internalModelValue = computed(() => props.modelValue);

const v$ = useVuelidate({
    field: props.validation ?? {},
}, {
    field: internalModelValue,
});

defineEmits<{
    (on:"update:modelValue", value: unknown): void
}>();

const name = inject(nameInjectionKey);
if (typeof name !== "string") {
    throw new Error();
}
</script>

<template>
    <div :class="radioType">
        <input
            :id="id || `${name}_${value}`"
            :class="['form-control radio infoItem btn-check', {'errorfield': v$.$error }]"
            :name="name"
            :value="value"
            :disabled="disabled"
            :checked="value === modelValue"
            type="radio"
            @change="[$emit('update:modelValue', value), v$.$touch()]"
        >
        <label
            :for="id || `${name}_${value}`"
            class="form-radio-label btn disable-selection align-middle show-pointer"
            :class="childClass ? childClass : ''"
        >
            {{ title }}
        </label>
    </div>
</template>

<style lang="scss">
    .standard {
        display: inline-block;
        padding-right:  15px;
        label.form-radio-label {
            padding: 11px 20px 9px;
            text-align: center;
            border-radius: 4px;
        }
    }
    .responsive {
        display: block;
        padding: 0;
        label.form-radio-label {
            padding: 11px 20px 9px;
            text-align: center;
            border-radius: 4px;
        }
    }
    .pill{
        display: inline-block;
        margin-left: 0px;
        &-start {
            border-top-right-radius: 0 !important;
            border-bottom-right-radius: 0 !important;
            border-top-left-radius: 25px !important;
            border-bottom-left-radius: 25px !important;
        }
        &-end{
            border-top-left-radius: 0 !important;
            border-bottom-left-radius: 0 !important;
            border-top-right-radius: 25px !important;
            border-bottom-right-radius: 25px !important;
        }
    }
    .block {
        display: block;
        width: 100%;
        border-radius: 0%;
        label.form-radio-label {
            padding: 20px 20px 20px;
            border-radius: 4px;
            min-height: 95px;
        }
    }
    @media only screen and (min-width: 992px) {
        .block {
            display: inline;
            padding-right: 15px;
            height: 110px;
        }
    }
    @media only screen and (min-width: 640px) {
        .standard, .responsive {
            display: inline-block;
            padding-right: 15px;
        }
    }
</style>
